import React, { useState } from "react";
import { useStaticQuery, graphql, Link, navigate } from "gatsby";
import { FaSearch } from "react-icons/fa";
import { buildCategoryPath } from "../../utils/path-utils";
import {
  categoryArchivePath,
  foldSearchText,
  searchResultsPath,
  tagArchivePath,
} from "../../utils/site-search";

interface Article {
  id: string;
  title: string;
  uri: string;
  type: "post" | "point" | "category" | "tag";
  categories?: {
    nodes: {
      slug: string;
      databaseId: number;
      parentDatabaseId?: number;
    }[];
  };
  icon?: string;
}

/**
 * Header search — autosuggest while typing; Enter opens /search/?q=…
 */
const SearchBar: React.FC = () => {
  const data = useStaticQuery(graphql`
    query ArticlesQuery {
      allWpPost {
        nodes {
          id
          title
          uri
          categories {
            nodes {
              slug
              databaseId
              parentDatabaseId
            }
          }
        }
      }
      allWpPoint {
        nodes {
          id
          title
          uri
          categories {
            nodes {
              slug
              databaseId
              parentDatabaseId
            }
          }
        }
      }
      allWpCategory {
        nodes {
          id
          name
          uri
          slug
          databaseId
          parentDatabaseId
        }
      }
      allWpTag {
        nodes {
          id
          name
          uri
          slug
        }
      }
    }
  `);

  const articles: Article[] = [
    ...data.allWpPost.nodes.map((post: any) => ({ ...post, type: "post" as const })),
    ...data.allWpPoint.nodes.map((point: any) => ({
      ...point,
      uri: point.uri.replace("/point/", "/"),
      type: "point" as const,
    })),
    ...data.allWpCategory.nodes.map((category: any) => ({
      id: category.id,
      title: category.name,
      uri: categoryArchivePath(category, data.allWpCategory.nodes),
      type: "category" as const,
      slug: category.slug,
    })),
    ...data.allWpTag.nodes.map((tag: any) => ({
      id: tag.id,
      title: tag.name,
      uri: tagArchivePath(tag.slug),
      type: "tag" as const,
      icon: `/images/icons/${tag.slug}.svg`,
      slug: tag.slug,
    })),
  ];
  const allCategories = data.allWpCategory.nodes;

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  /** Enter opens a suggestion only after ArrowUp/ArrowDown — not after mouse hover. */
  const [keyboardSelect, setKeyboardSelect] = useState(false);

  const articleHref = (article: Article) => {
    if (article.type === "category" || article.type === "tag") return article.uri;
    if (article.categories?.nodes?.length) {
      return `/${buildCategoryPath(article.categories.nodes[0], allCategories)}${article.uri}`;
    }
    return article.uri;
  };

  const goToResults = (term: string) => {
    const q = term.trim();
    if (!q) return;
    setIsFocused(false);
    setFilteredArticles([]);
    navigate(searchResultsPath(q));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    setHighlightedIndex(-1);
    setKeyboardSelect(false);
    const needle = foldSearchText(term.trim());
    if (needle) {
      setFilteredArticles(
        articles
          .filter((article) => foldSearchText(article.title).includes(needle))
          .slice(0, 12)
      );
    } else {
      setFilteredArticles([]);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setFilteredArticles([]);
    setHighlightedIndex(-1);
    setKeyboardSelect(false);
    setIsFocused(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
    }, 180);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!filteredArticles.length) return;
      setKeyboardSelect(true);
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, filteredArticles.length - 1)
      );
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setKeyboardSelect(true);
      setHighlightedIndex((prev) => Math.max(prev - 1, -1));
      return;
    }
    if (e.key === "Escape") {
      clearSearch();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      // Only open a suggestion after keyboard arrow selection
      if (
        keyboardSelect &&
        highlightedIndex >= 0 &&
        filteredArticles[highlightedIndex]
      ) {
        navigate(articleHref(filteredArticles[highlightedIndex]));
        clearSearch();
        return;
      }
      goToResults(searchTerm);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      keyboardSelect &&
      highlightedIndex >= 0 &&
      filteredArticles[highlightedIndex]
    ) {
      navigate(articleHref(filteredArticles[highlightedIndex]));
      clearSearch();
      return;
    }
    goToResults(searchTerm);
  };

  return (
    <div className="search-container">
      <form className="input-wrapper" onSubmit={handleSubmit} role="search">
        <span className="magnifier-icon" aria-hidden="true">
          <FaSearch />
        </span>
        <input
          type="search"
          placeholder="Search places…"
          value={searchTerm}
          id="search"
          name="q"
          aria-label="Search places"
          aria-controls="search-results"
          aria-autocomplete="list"
          autoComplete="off"
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
          style={{
            background: "#053800",
            fontWeight: 600,
            border: "1px solid #053800",
            color: "#fff",
            outline: "none",
          }}
        />
      </form>

      {searchTerm && (
        <button
          type="button"
          className="clear-button"
          onClick={clearSearch}
          aria-label="Clear search"
        >
          ✕
        </button>
      )}

      <ul
        id="search-results"
        className="search-results"
        role="listbox"
        aria-label="Suggestions"
      >
        {isFocused && filteredArticles.length > 0 && (
          <>
            {filteredArticles.map((article, index) => (
              <li
                key={article.id}
                role="option"
                aria-selected={highlightedIndex === index}
                tabIndex={-1}
                className={`result-item ${
                  highlightedIndex === index ? "highlighted" : ""
                }`}
                onMouseEnter={() => {
                  setKeyboardSelect(false);
                  setHighlightedIndex(index);
                }}
              >
                <Link to={articleHref(article)} onMouseDown={(e) => e.preventDefault()}>
                  {article.icon && (
                    <img
                      src={article.icon}
                      alt=""
                      style={{
                        width: "16px",
                        height: "16px",
                        marginRight: "8px",
                      }}
                    />
                  )}
                  {article.title}
                </Link>
              </li>
            ))}
            <li className="result-item result-item--all">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => goToResults(searchTerm)}
              >
                See all results for “{searchTerm.trim()}”
              </button>
            </li>
          </>
        )}
      </ul>
    </div>
  );
};

export default SearchBar;
